import {Box, Center, Column, Spacer, Text, useDisclose} from 'native-base';
import {CreateTeamProvider, useCreateTeam} from '@ui/provider/CreateTeamProvider';
import React, {useEffect} from 'react';
import {UserProvider, useUser} from '@ui/provider/user/UserProvider';
import BottomSheetScrollView from '@ui/components/gorhom/BottomSheetScrollView';
import Bottomsheet from '@ui/components/sheet/Bottomsheet';
import BottomsheetProps from '@ui/components/sheet/BottomsheetProps';
import CreateTeamButton from './CreateTeamButton';
import TeamCreateModal from '../create/TeamCreateModal';
import UserTeams from './UserTeams';
import {useTranslation} from 'react-i18next';

const contentContainerStyle = {
    flex: 1,
};

interface Props {
    onTeamSelect: (teamId: string) => Promise<void>;
    onPressCreateTeam: () => void;
}

const TeamSelect = (props: Props) => {
    const {onTeamSelect, onPressCreateTeam} = props;
    const {t} = useTranslation();
    const {clearData} = useCreateTeam();
    const [teamIdLoading, setTeamIdLoading] = React.useState('');
    const {user} = useUser();
    const teams = Object.values(user?.teams ?? {});

    useEffect(() => {
        clearData();
    }, []);

    const onPress = async(teamId: string) => {
        setTeamIdLoading(teamId);
        await onTeamSelect(teamId);
        setTeamIdLoading('');
    };

    const hasTeams = !!teams.length;

    return (
        <BottomSheetScrollView contentContainerStyle={hasTeams ? {} : contentContainerStyle}>
            <Column
                alignSelf='center'
                px={4}
                safeAreaBottom
                flex={1}
                w='full'
            >
                <Text variant='bodyLg'>{t('team.select.description')}</Text>

                <Box
                    h={{
                        base: 8,
                        lg: 10,
                    }}
                />

                {!hasTeams &&
                    <Center flex={1}>
                        <Box rounded='lg' bg='info.100' p='4' w='full'>
                            <Text variant='mediumSemiBold' color='info.800'>{t('team.select.invites.description')}</Text>

                            <Text variant='bodyLg' color='info.800'>{t('team.select.invites.descriptionSubtitle')}</Text>
                        </Box>
                    </Center>
                }

                {hasTeams && <UserTeams teams={teams} onPress={onPress} teamIdLoading={teamIdLoading} _item={{bg: 'blacks.100'}} />
                }

                {hasTeams && <Spacer />}

                <CreateTeamButton onPress={onPressCreateTeam}/>
            </Column>
        </BottomSheetScrollView>
    );
};

interface ModalProps extends Omit<BottomsheetProps, 'children'> {
    onTeamSelect: (teamId: string) => Promise<void>;
}

export const TeamSelectModal = (props: ModalProps) => {
    const userProvider = useUser();
    const {isOpen: isCreateTeamOpen, onOpen: onOpenCreateTeam, onClose: onCloseCreateTeam} = useDisclose();
    const {t} = useTranslation();

    const onOpenCreateTeamModal = () => {
        props.onClose();
        onOpenCreateTeam();
    };

    const onTeamCreated = async(teamId: string) => {
        onCloseCreateTeam();
        await props.onTeamSelect(teamId);
    };

    return (
        <>
            <Bottomsheet {...props} _bottomSheetProps={{snapPoints: ['70%']}} title={t('competition.detail.selectTeam')}>
                <UserProvider value={userProvider}>
                    <CreateTeamProvider>
                        <TeamSelect onTeamSelect={props.onTeamSelect} onPressCreateTeam={onOpenCreateTeamModal}/>

                    </CreateTeamProvider>
                </UserProvider>
            </Bottomsheet>

            <TeamCreateModal onTeamCreated={onTeamCreated} isOpen={isCreateTeamOpen} onClose={onCloseCreateTeam}/>
        </>
    );
};

export default TeamSelect;
